import { ChangeEvent, DragEvent, ReactNode, useState } from 'react'
import { cn } from '@global/utils/tailwind'
import UploadSVG from 'images/upload.svg'

interface DragNDropProps {
  accept: string
  isMultiple?: boolean
  title?: string | ReactNode
  description?: string | ReactNode
  children?: ReactNode
  containerClassName?: string
  className?: string
  isError?: boolean
  onFilesChange: (files: File[]) => void
}

export const DragNDrop = ({
  title,
  description,
  accept,
  children,
  isMultiple = false,
  containerClassName,
  className,
  onFilesChange,
  isError = false,
}: DragNDropProps) => {
  const [isDraggedOver, setIsDraggedOver] = useState(false)

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      const newFiles = Array.from(event.target.files)

      onFilesChange(newFiles)
    }
  }

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()

    if (event.dataTransfer.files?.length) {
      const newFiles = Array.from(event.dataTransfer.files)

      const validFiles = newFiles.filter(file =>
        accept.split(',').some(acceptType => {
          const trimmedAcceptType = acceptType.trim()
          const isWildcard = trimmedAcceptType === '*/*'
          const isFileType = file.type === trimmedAcceptType
          const isExtension = file.name.endsWith(trimmedAcceptType.replace('.', ''))

          return isWildcard || isFileType || isExtension
        }),
      )

      onFilesChange(validFiles)
    }
  }

  const onDragOver = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!isDraggedOver) setIsDraggedOver(true)
  }

  const onDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (isDraggedOver) setIsDraggedOver(false)
  }

  return (
    <div className={cn('flex items-center justify-center w-full', containerClassName)}>
      <label
        htmlFor="dropzone-file"
        className={cn(
          'flex flex-col items-center justify-center w-full min-h-64 border border-dashed rounded-lg cursor-pointer hover:bg-cv-blue-50 hover:border-cv-primary',
          {
            'border-red-500 bg-red-50': isError && !isDraggedOver,
            'border-cv-primary bg-cv-blue-50': isDraggedOver,
            'border-cv-blue-200 bg-white': !isDraggedOver,
          },
          className,
        )}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        css={{
          '& *': {
            'pointer-events': 'none',
          },
        }}
      >
        {children || (
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <UploadSVG height="120px" viewBox="0 0 402 218" className="my-4" />
            <p className="mt-3 text-[14px] text-slate-800">{title}</p>
            {!!description && <p className="text-[12px] text-gray-500 ">{description}</p>}
          </div>
        )}

        <input
          id="dropzone-file"
          type="file"
          className="hidden"
          multiple={isMultiple}
          onChange={onChange}
          accept={accept}
        />
      </label>
    </div>
  )
}
